import {
  CREDENTIAL_PROVISIONABLE_QUALIFICATIONS,
  type CredentialProvisionableQualification,
  type CredentialProvisionRequestType,
} from "@clipboard-health/contract-backend-main";
import { LoadingButton, Text, Title } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useEffect } from "react";

import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logError, logEvent } from "../lib/analytics";
import { useCreateWorkerCredentialProvisionRequest } from "./api/useCreateWorkerCredentialProvisionRequest";
import { useChartingCredentialProvisioningConfig } from "./api/useCredentialProvisioningConfig";
import { useDefaultQualificationForProvisioning } from "./api/useDefaultQualificationForProvisioning";
import { useGetWorkerCredentialProvisionRequests } from "./api/useGetWorkerCredentialProvisionRequests";

interface CreateOrResetCredentialsModalProps {
  onClose: () => void;
  workerName: string | undefined;
  onAction: () => void;
  workerId: string;
  workplaceId: string;
}

const actionToText: Record<CredentialProvisionRequestType, { label: string; description: string }> =
  {
    create_credentials: {
      label: "Create",
      description: "This will create a new account for the professional.",
    },
    reset_password: {
      label: "Reset",
      description:
        "This will reset the account to your workplace's default one-time password, which will be sent to the professional.",
    },
  };

function isQualificationProvisionable(
  qualification?: string
): qualification is CredentialProvisionableQualification {
  return (
    isDefined(qualification) &&
    (CREDENTIAL_PROVISIONABLE_QUALIFICATIONS as readonly string[]).includes(qualification)
  );
}

export function CreateOrResetCredentialsModal({
  onClose,
  workerName,
  onAction,
  workerId,
  workplaceId,
}: CreateOrResetCredentialsModalProps) {
  const { mutate: createWorkerCredentialRequest, isLoading: isCreatingWorkerCredentialRequest } =
    useCreateWorkerCredentialProvisionRequest();

  const { data: provisionRequests, isLoading: isLoadingProvisionRequests } =
    useGetWorkerCredentialProvisionRequests({
      workerId,
      workplaceId,
    });

  const hasExistingProvisionerAccount =
    provisionRequests?.data.some((request) => request.attributes.status === "succeeded") ?? false;

  const requestType: CredentialProvisionRequestType = hasExistingProvisionerAccount
    ? "reset_password"
    : "create_credentials";

  const {
    qualification,
    isLoading: isLoadingDefaultQualification,
    isError: isErrorDefaultQualification,
  } = useDefaultQualificationForProvisioning({ workerId, workplaceId });

  const { enabled: isChartingCredentialProvisioningEnabled } =
    useChartingCredentialProvisioningConfig();

  useEffect(() => {
    if (!isLoadingDefaultQualification && !isDefined(qualification)) {
      showErrorToast("Failed to load worker qualification");
    }
  }, [isLoadingDefaultQualification, qualification]);

  if (!isChartingCredentialProvisioningEnabled) {
    return null;
  }

  const actionText = actionToText[requestType];

  const title = isDefined(workerName)
    ? `${actionText.label} PointClickCare account for ${workerName}?`
    : `${actionText.label} PointClickCare account?`;

  const isLoading =
    isLoadingProvisionRequests ||
    isCreatingWorkerCredentialRequest ||
    isLoadingDefaultQualification;

  const isActionDisabled = isErrorDefaultQualification || !isDefined(qualification);

  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={() => {
        onClose();
      }}
    >
      <DialogTitle>
        <Stack direction="column" spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Title component="h2" variant="h3">
              {title}
            </Title>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Text variant="body1">{actionText.description}</Text>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            color: (theme) => theme.palette.text.primary,
            paddingX: 4,
            paddingY: 1,
          }}
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          disabled={isActionDisabled}
          isLoading={isLoading}
          variant="contained"
          sx={{
            paddingX: 4,
            paddingY: 1,
          }}
          onClick={() => {
            if (isQualificationProvisionable(qualification)) {
              logEvent(APP_V2_USER_EVENTS.WORKER_CREDENTIAL_PROVISION_REQUEST_CREATED, {
                workerId,
                workplaceId,
                qualification,
                requestType,
              });
              createWorkerCredentialRequest({ workerId, workplaceId, qualification, requestType });
              onAction();
            } else {
              showErrorToast("Unable to provision credentials for worker");
              logError(APP_V2_APP_EVENTS.QUALIFICATION_NOT_PROVISIONABLE_FAILURE, {
                error: "Qualification is not provisionable",
                metadata: {
                  workerId,
                  workplaceId,
                  qualification,
                },
              });
            }
          }}
        >
          {actionText.label}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
